<script lang="ts">
  import { goto } from '$app/navigation';
  import { page } from '$app/stores';

  import { onMount } from 'svelte';

  import { InstitutionType } from '@do/models';

  import { Accordion } from '@shared/ui/accordion';
  import { BurgerButton } from '@shared/ui/buttons/burger';
  import { Button } from '@shared/ui/buttons/button';
  import { RangeSlider } from '@shared/ui/fields/range-slider';
  import { SelectBox } from '@shared/ui/fields/select-box';
  import { Icon } from '@shared/ui/icon';
  import { RadioToggle } from '@shared/ui/radio-toggle';
  import { Sidebar } from '@shared/ui/sidebar';
  import { Switch } from '@shared/ui/switch';
  import { theme, ThemeModes } from '@shared/ui/theme';
  import { cn, debounce, is } from '@shared/utils';
  import { formatNumber } from '@shared/utils/number';

  import { ArtAssetCardApi } from '@entities/art-asset';
  import { CuratorBadge } from '@entities/curator';

  import { ArtworksFilterKey, artworksViewOptions, toSlug } from '../configs';
  import { artworksFilterSidebarActivity } from '../model';
  import { artworksFiltersModel } from '../model';
  import type { FormOptions } from '../types';

  const artAssetCardApi = new ArtAssetCardApi(fetch);
  let isLoading = false;

  const formatter = (value: number) => formatNumber(value);

  const onClose = () => {
    artworksFilterSidebarActivity.close();
  };

  const fetchData = async () => {
    isLoading = true;

    const response = await artAssetCardApi.readFiltersOptions(
      $artworksFiltersModel.values,
      $artworksFiltersModel.initialValues,
    );
    isLoading = false;

    return response;
  };

  const handleApply = () => {
    const slug = toSlug($artworksFiltersModel.values, $artworksFiltersModel.initialValues);
    goto(`/artworks${slug}/`);
    onClose();
  };

  const handleFetch = () => {
    fetchData().then((res) => {
      if (!res) return;
      artworksFiltersModel.updateData(res, $artworksFiltersModel.values);
    });
  };

  const initialFetch = async () => {
    isLoading = true;
    const response = await artAssetCardApi.readFiltersOptions();
    artworksFiltersModel.resetValues();
    if (response) artworksFiltersModel.initData(response, $page.params.slug);
    isLoading = false;
  };

  const handleClear = () => {
    artworksFiltersModel.resetValues();
  };

  const onParamChange = (key: ArtworksFilterKey, e: CustomEvent) => {
    $artworksFiltersModel.values[key] = e.detail;

    const keyNoSlugs = key.replace('_slugs', '') as keyof FormOptions;
    artworksFiltersModel.updateCurrentOption({ [keyNoSlugs]: $artworksFiltersModel.options[keyNoSlugs] });
  };

  // const onPriceChangeStart = () => {
  //   artworksFiltersModel.updateCurrentOption({ price: $artworksFiltersModel.options.price });
  // };

  // const onPriceChangeEnd = (e: CustomEvent) => {
  //   $artworksFiltersModel.values.price = e.detail.values;
  // };

  const onYearsChangeStart = () => {
    artworksFiltersModel.updateCurrentOption({ creation_year: $artworksFiltersModel.options.creation_year });
  };

  const onYearsChangeEnd = (e: CustomEvent) => {
    $artworksFiltersModel.values.creation_year = e.detail.values;
  };

  const onOriginalSizeXChangeStart = () => {
    artworksFiltersModel.updateCurrentOption({ original_size_x: $artworksFiltersModel.options.original_size_x });
  };

  const onOriginalSizeXChangeEnd = (e: CustomEvent) => {
    $artworksFiltersModel.values.original_size_x = e.detail.values;
  };

  const onOriginalSizeYChangeStart = () => {
    artworksFiltersModel.updateCurrentOption({ original_size_y: $artworksFiltersModel.options.original_size_y });
  };

  const onOriginalSizeYChangeEnd = (e: CustomEvent) => {
    $artworksFiltersModel.values.original_size_y = e.detail.values;
  };

  const debounced = debounce(handleFetch);

  onMount(() => {
    initialFetch();
  });

  $: ((val, inVal) => {
    if (JSON.stringify(val) === JSON.stringify(inVal)) return;
    debounced();
  })($artworksFiltersModel.values, $artworksFiltersModel.updatedValues);

  $: applyText =
    $artworksFiltersModel.items_total_count === 1
      ? `Show (${$artworksFiltersModel.items_total_count}) artwork`
      : `Show (${$artworksFiltersModel.items_total_count}) artworks`;

  let isAccordionOpen = true;
  function toggleAccordion() {
    isAccordionOpen = !isAccordionOpen;
  }
</script>

<Sidebar sidebarActivity="{$artworksFilterSidebarActivity}" handleClose="{onClose}">
  <div class="{cn('filter-sidebar flex h-full flex-col', { dark: $theme === ThemeModes.Dark })}">
    <div class="flex items-center justify-between px-6 py-[22px]">
      <span class="text-default-900 dark:text-dark-900 font-inter text-xl font-semibold">Filter by criteria</span>
      <span class="flex gap-4">
        <Button size="sm" variant="secondary" iconName="trash" iconPosition="left" hugged on:click="{handleClear}">
          Clear
        </Button>

        <span class="flex items-center md:hidden">
          <BurgerButton isActive on:click="{onClose}" />
        </span>
      </span>
    </div>

    <div class="no-scrollbar flex-grow space-y-4 overflow-auto px-5 py-6">
      <div class="space-y-3">
        <div class="dark:text-dark-900 text-base font-medium">by Sale status</div>
        <div>
          <RadioToggle
            name="artworksView"
            options="{artworksViewOptions}"
            bind:value="{$artworksFiltersModel.values.token_status}"
          />
        </div>
      </div>

      <div class="space-y-3">
        <div class="flex items-center gap-2">
          <Switch bind:isChecked="{$artworksFiltersModel.values.curator_review}" />
          <Icon name="four-pointed-star" />
          <span>Curators choice</span>
          <CuratorBadge iconName="info" />
        </div>
      </div>

      {#if $page.data.configurations.type !== InstitutionType.Artist}
        <div class="space-y-3">
          <div class="dark:text-dark-900 text-base font-medium">Artist(s)</div>
          <div>
            <SelectBox
              options="{$artworksFiltersModel.options.artists}"
              selectedOptions="{$artworksFiltersModel.values.artists_slugs}"
              isMulti
              on:change="{(e) => onParamChange(ArtworksFilterKey.Artists, e)}"
            />
          </div>
        </div>
      {/if}

      <div class="space-y-3">
        <div class="dark:text-dark-900 text-base font-medium">Styles</div>
        <div>
          <SelectBox
            options="{$artworksFiltersModel.options.styles}"
            selectedOptions="{$artworksFiltersModel.values.styles_slugs}"
            isMulti
            on:change="{(e) => onParamChange(ArtworksFilterKey.Styles, e)}"
          />
        </div>
      </div>

      <div class="space-y-3">
        <div class="dark:text-dark-900 text-base font-medium">Medium</div>
        <div>
          <SelectBox
            options="{$artworksFiltersModel.options.medium}"
            selectedOptions="{$artworksFiltersModel.values.medium_slugs}"
            isMulti
            on:change="{(e) => onParamChange(ArtworksFilterKey.Medium, e)}"
          />
        </div>
      </div>
      <!-- TODO: removed for now -->
      <!--      <div class="space-y-3">-->
      <!--        <div class="dark:text-dark-900 text-base font-medium">Country of origin</div>-->
      <!--        <div>-->
      <!--          <SelectBox-->
      <!--            options="{$artworksFiltersModel.options.countries}"-->
      <!--            selectedOptions="{$artworksFiltersModel.values.countries_slugs}"-->
      <!--            isMulti-->
      <!--            on:change="{(e) => onParamChange(ArtworksFilterKey.Countries, e)}"-->
      <!--          />-->
      <!--        </div>-->
      <!--      </div>-->
      <!-- <div class="space-y-3">
        <div class="dark:text-dark-900 text-base font-medium">Price range (USD)</div>
        <div>
          <RangeSlider
            min="{$artworksFiltersModel.options.price[0]}"
            max="{$artworksFiltersModel.options.price[1]}"
            values="{$artworksFiltersModel.values.price}"
            {formatter}
            on:start="{onPriceChangeStart}"
            on:stop="{onPriceChangeEnd}"
          />
        </div>
      </div> -->

      <div class="space-y-3">
        <div class="dark:text-dark-900 text-base font-medium">Years</div>
        <div>
          <RangeSlider
            min="{$artworksFiltersModel.options.creation_year[0]}"
            max="{$artworksFiltersModel.options.creation_year[1]}"
            values="{$artworksFiltersModel.values.creation_year}"
            on:start="{onYearsChangeStart}"
            on:stop="{onYearsChangeEnd}"
          />
        </div>
      </div>

      <div class="space-y-3 pt-4">
        <button
          on:click="{toggleAccordion}"
          class="text-default-900 dark:text-dark-900 mb-4 flex w-full items-center justify-between text-left text-sm font-medium focus:outline-none"
        >
          <div class="dark:text-dark-900 text-base font-medium">Dimensions (cm)</div>
          <Icon name="caretDown" classes="transform transition {is(!isAccordionOpen, 'rotate-180')}" />
        </button>

        <Accordion isOpen="{isAccordionOpen}">
          <div class="space-y-3">
            <div class="dark:text-dark-900 text-base font-medium">Width (X)</div>
            <div>
              <RangeSlider
                min="{$artworksFiltersModel.options.original_size_x[0]}"
                max="{$artworksFiltersModel.options.original_size_x[1]}"
                values="{$artworksFiltersModel.values.original_size_x}"
                {formatter}
                on:start="{onOriginalSizeXChangeStart}"
                on:stop="{onOriginalSizeXChangeEnd}"
                rest="{false}"
              />
            </div>
          </div>

          <div class="space-y-3">
            <div class="dark:text-dark-900 text-base font-medium">Height (Y)</div>
            <div>
              <RangeSlider
                min="{$artworksFiltersModel.options.original_size_y[0]}"
                max="{$artworksFiltersModel.options.original_size_y[1]}"
                values="{$artworksFiltersModel.values.original_size_y}"
                {formatter}
                on:start="{onOriginalSizeYChangeStart}"
                on:stop="{onOriginalSizeYChangeEnd}"
                rest="{false}"
              />
            </div>
          </div>
        </Accordion>
      </div>
    </div>
    <div class="px-6 py-[22px]">
      <Button size="md" on:click="{handleApply}" disabled="{isLoading || $artworksFiltersModel.items_total_count === 0}"
        >{applyText}</Button
      >
    </div>
  </div>
</Sidebar>
